import React, { useState, useEffect } from "react";
import axios from 'axios';

import BleedingDisorderStatisticsSortingAlgorithm from "../../../../data/BleedingDisorderStatisticsSortingAlgorithm";

// Styles
import "./BleedingDisorderStatistics.css";
import StatisticCategoryViewer from "./StatisticCategoryViewer";

// Display values
const noSelection = 0;
const hemoASelected = 1;
const hemoBSelected = 2;
const hemoCSelected = 3;
const VWDSelected = 4;
const fac1Selected = 5;
const fac2Selected = 6;
const fac5Selected = 7;
const fac7Selected = 8;
const fac10Selected = 9;
const fac12Selected = 10;
const fac13Selected = 11;
const allSelected = 12;

// Print button for getting missing bleeding disorder info, shouldn't be needed again but keeping it just
// in case.
// <button className="bleeding-disorder-statistics-nav-category-button approved-button" onClick={() => {
//     printMissingInfo();
// }}>
//     Print
// </button>

// const printMissingInfo = () => {
//     axios.get('/api/get-missing-data-bleeding-disorder-contacts', { withCredentials: true })
//         .then((res) => {
//         })
// }


// Takes applications as an input for processing if needed
export default function BleedingDisorderStatistics(props) {
    const applications = props.applications;
    const [statsCalculated, setStatsCalculated] = useState(false);
    const [bleedingDisorderStats, setBleedingDisorderStats] = useState(null);
    const [selectedDisorder, setSelectedDisorder] = useState(noSelection);
    const [selectedDisorderAmount, setSelectedDisorderAmount] = useState(0);
    const [selectedDisorderContacts, setSelectedDisorderContacts] = useState({});
    const [categoryHeader, setCategoryHeader] = useState('');

    // Initial load flag for preventing inCategory useEffect from updating prematurely
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    // State for managing back button
    const [inCategory, setInCategory] = useState(false);

    // Access
    const [applicationAccess, setApplicationAccess] = useState(null);

    const updateBleedingDisorderStats = () => {
        const updatedStats = BleedingDisorderStatisticsSortingAlgorithm(applications);
        setBleedingDisorderStats(updatedStats);
        const formData = new FormData();
        formData.append('updatedStats', JSON.stringify(updatedStats));
        axios.post('/api/update-bleeding-disorder-statistics', formData, { withCredientials: true })
            .then((res) => {
                setInitialLoadComplete(true);
            })
    }

    useEffect(() => {
        axios.defaults.withCredentials = true;
        if (!statsCalculated) {
            axios.get('/api/last-bleeding-disorder-statistics-entry', { withCredentials: true })
                .then((res) => {
                    if (res.data.existingEntry) {
                        setBleedingDisorderStats(res.data.statistics);
                        setInitialLoadComplete(true);
                    } else {
                        updateBleedingDisorderStats();
                    }
                })
        }
    }, []);

    // Update selection when back button is pressed within StatisticCategoryViewer
    useEffect(() => {
        if (initialLoadComplete && !inCategory) {
            updateSelection(noSelection);
        }
    }, [inCategory])

    // Update selection based off internal values and update states according for child component to utilize
    const updateSelection = (selectedValue) => {
        switch (selectedValue) {

            // Essentially default case, resets variables to their default state 
            case noSelection: {
                setSelectedDisorderAmount(0);
                setSelectedDisorderContacts({});
                setCategoryHeader('');
            }

            // Hemophilia A
            case hemoASelected: {
                // Create variables for attaching to useStates
                const localAmount = bleedingDisorderStats.hemoAPatients + bleedingDisorderStats.hemoACaretakers
                    + bleedingDisorderStats.hemoAFamilyMembers;
                const localContacts = {
                    patients: bleedingDisorderStats.hemoAPatientContacts,
                    caretakers: bleedingDisorderStats.hemoACaretakerContacts,
                    familyMembers: bleedingDisorderStats.hemoAFamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Hemophilia A Statistics');

                // Update internal selection value
                setSelectedDisorder(hemoASelected);
                break;
            }

            // Hemophilia B
            case hemoBSelected: {
                // Create variables for attaching to useStates
                const localAmount = bleedingDisorderStats.hemoBPatients + bleedingDisorderStats.hemoBCaretakers
                    + bleedingDisorderStats.hemoBFamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.hemoBPatientContacts,
                    caretakers: bleedingDisorderStats.hemoBCaretakerContacts,
                    familyMembers: bleedingDisorderStats.hemoBFamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Hemophilia B Statistics');

                // Update internal selection value
                setSelectedDisorder(hemoBSelected);
                break;
            }

            // Hemophilia C
            case hemoCSelected: {
                // Create variables for attaching to useStates
                const localAmount = bleedingDisorderStats.hemoCPatients + bleedingDisorderStats.hemoCCaretakers
                    + bleedingDisorderStats.hemoCFamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.hemoCPatientContacts,
                    caretakers: bleedingDisorderStats.hemoCCaretakerContacts,
                    familyMembers: bleedingDisorderStats.hemoCFamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Hemophilia C Statistics');

                // Update internal selection value
                setSelectedDisorder(hemoCSelected);
                break;
            }

            // VWD
            case VWDSelected: {
                // Create variables for attaching to useStates
                const localAmount = bleedingDisorderStats.VWDPatients + bleedingDisorderStats.VWDCaretakers
                    + bleedingDisorderStats.VWDFamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.VWDPatientContacts,
                    caretakers: bleedingDisorderStats.VWDCaretakerContacts,
                    familyMembers: bleedingDisorderStats.VWDFamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('VWD Statistics');

                // Update internal selection value
                setSelectedDisorder(VWDSelected);
                break;
            }

            // Factor 1
            case fac1Selected: {
                // Create variables for attaching to useStates
                const localAmount = bleedingDisorderStats.fac1Patients + bleedingDisorderStats.fac1Caretakers
                    + bleedingDisorderStats.fac1FamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.fac1PatientContacts,
                    caretakers: bleedingDisorderStats.fac1CaretakerContacts,
                    familyMembers: bleedingDisorderStats.fac1FamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);


                // Update Header value
                setCategoryHeader('Factor 1 Statistics');

                // Update internal selection value
                setSelectedDisorder(fac1Selected);
                break;
            }

            // Factor 2
            case fac2Selected: {
                const localAmount = bleedingDisorderStats.fac2Patients + bleedingDisorderStats.fac2Caretakers
                    + bleedingDisorderStats.fac2FamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.fac2PatientContacts,
                    caretakers: bleedingDisorderStats.fac2CaretakerContacts,
                    familyMembers: bleedingDisorderStats.fac2FamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Factor 2 Statistics');

                // Update internal selection value
                setSelectedDisorder(fac2Selected);
                break;
            }

            // Factor 5
            case fac5Selected: {
                const localAmount = bleedingDisorderStats.fac5Patients + bleedingDisorderStats.fac5Caretakers
                    + bleedingDisorderStats.fac5FamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.fac5PatientContacts,
                    caretakers: bleedingDisorderStats.fac5CaretakerContacts,
                    familyMembers: bleedingDisorderStats.fac5FamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Factor 5 Statistics');

                // Update internal selection value
                setSelectedDisorder(fac5Selected);
                break;
            }

            // Factor 7
            case fac7Selected: {
                const localAmount = bleedingDisorderStats.fac7Patients + bleedingDisorderStats.fac7Caretakers
                    + bleedingDisorderStats.fac7FamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.fac7PatientContacts,
                    caretakers: bleedingDisorderStats.fac7CaretakerContacts,
                    familyMembers: bleedingDisorderStats.fac7FamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Factor 7 Statistics');

                // Update internal selection value
                setSelectedDisorder(fac7Selected);
                break;
            }

            // Factor 10
            case fac10Selected: {
                const localAmount = bleedingDisorderStats.fac10Patients + bleedingDisorderStats.fac10Caretakers
                    + bleedingDisorderStats.fac10FamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.fac10PatientContacts,
                    caretakers: bleedingDisorderStats.fac10CaretakerContacts,
                    familyMembers: bleedingDisorderStats.fac10FamilyMemberContacts,
                };
                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Factor 10 Statistics');

                // Update internal selection value
                setSelectedDisorder(fac10Selected);
                break;
            }

            // Factor 12
            case fac12Selected: {
                const localAmount = bleedingDisorderStats.fac12Patients + bleedingDisorderStats.fac12Caretakers
                    + bleedingDisorderStats.fac12FamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.fac12PatientContacts,
                    caretakers: bleedingDisorderStats.fac12CaretakerContacts,
                    familyMembers: bleedingDisorderStats.fac12FamilyMemberContacts,
                };
                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Factor 12 Statistics');

                // Update internal selection value
                setSelectedDisorder(fac12Selected);
                break;
            }

            // Factor 13
            case fac13Selected: {
                const localAmount = bleedingDisorderStats.fac13Patients + bleedingDisorderStats.fac13Caretakers
                    + bleedingDisorderStats.fac13FamilyMembers;

                const localContacts = {
                    patients: bleedingDisorderStats.fac13PatientContacts,
                    caretakers: bleedingDisorderStats.fac13CaretakerContacts,
                    familyMembers: bleedingDisorderStats.fac13FamilyMemberContacts,
                };

                // Save local variables to states
                setSelectedDisorderAmount(localAmount);
                setSelectedDisorderContacts(localContacts);

                // Update Header value
                setCategoryHeader('Factor 13 Statistics');

                // Update internal selection value
                setSelectedDisorder(fac13Selected);
                break;
            }

            default:
                break;
        }
    }

    if (bleedingDisorderStats === null) {
        return (
            <>
                <h1 className="header bleeding-disorder-stats-loading-text">Loading...</h1>
            </>
        )
    }

    const renderStatisticCategoryViewer = () => {
        if (selectedDisorder !== noSelection) {
            return <StatisticCategoryViewer amount={selectedDisorderAmount} contacts={selectedDisorderContacts}
                categoryHeader={categoryHeader} setInCategory={setInCategory} />
        }
    }

    const renderDisorderNav = () => {
        if (!inCategory) {
            return (
                <>
                    <div className="bleeding-disorder-statistics-nav">
                        <button className="bleeding-disorder-statistics-nav-category-button approved-button" onClick={() => {
                            updateSelection(allSelected);
                        }}>
                            All
                        </button>

                        <button className={bleedingDisorderStats.hemoAPatients > 0 || bleedingDisorderStats.hemoACaretakers > 0 ||
                            bleedingDisorderStats.hemoAFamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.hemoAPatients > 0 || bleedingDisorderStats.hemoACaretakers > 0 ||
                                    bleedingDisorderStats.hemoAFamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== hemoASelected) {
                                        updateSelection(hemoASelected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            A
                        </button>

                        <button className={bleedingDisorderStats.hemoBPatients > 0 || bleedingDisorderStats.hemoBCaretakers > 0 ||
                            bleedingDisorderStats.hemoBFamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.hemoBPatients > 0 || bleedingDisorderStats.hemoBCaretakers > 0 ||
                                    bleedingDisorderStats.hemoBFamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== hemoBSelected) {
                                        updateSelection(hemoBSelected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            B
                        </button>

                        <button className={bleedingDisorderStats.hemoCPatients > 0 || bleedingDisorderStats.hemoCCaretakers > 0 ||
                            bleedingDisorderStats.hemoCFamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.hemoCPatients > 0 || bleedingDisorderStats.hemoCCaretakers > 0 ||
                                    bleedingDisorderStats.hemoCFamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== hemoCSelected) {
                                        updateSelection(hemoCSelected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            C
                        </button>

                        <button className={bleedingDisorderStats.VWDPatients > 0 || bleedingDisorderStats.VWDCaretakers > 0 ||
                            bleedingDisorderStats.VWDFamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.VWDPatients > 0 || bleedingDisorderStats.VWDCaretakers > 0 ||
                                    bleedingDisorderStats.VWDFamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== VWDSelected) {
                                        updateSelection(VWDSelected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            VWD
                        </button>

                        <button className={bleedingDisorderStats.fac1Patients > 0 || bleedingDisorderStats.fac1Caretakers > 0 ||
                            bleedingDisorderStats.fac1FamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.fac1Patients > 0 || bleedingDisorderStats.fac1Caretakers > 0 ||
                                    bleedingDisorderStats.fac1FamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== fac1Selected) {
                                        updateSelection(fac1Selected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            1
                        </button>

                        <button className={bleedingDisorderStats.fac2Patients > 0 || bleedingDisorderStats.fac2Caretakers > 0 ||
                            bleedingDisorderStats.fac2FamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.fac2Patients > 0 || bleedingDisorderStats.fac2Caretakers > 0 ||
                                    bleedingDisorderStats.fac2FamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== fac2Selected) {
                                        updateSelection(fac2Selected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            2
                        </button>

                        <button className={bleedingDisorderStats.fac5Patients > 0 || bleedingDisorderStats.fac5Caretakers > 0 ||
                            bleedingDisorderStats.fac5FamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.fac5Patients > 0 || bleedingDisorderStats.fac5Caretakers > 0 ||
                                    bleedingDisorderStats.fac5FamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== fac5Selected) {
                                        updateSelection(fac5Selected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            5
                        </button>

                        <button className={bleedingDisorderStats.fac7Patients > 0 || bleedingDisorderStats.fac7Caretakers > 0 ||
                            bleedingDisorderStats.fac7FamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.fac7Patients > 0 || bleedingDisorderStats.fac7Caretakers > 0 ||
                                    bleedingDisorderStats.fac7FamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== fac7Selected) {
                                        updateSelection(fac7Selected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            7
                        </button>

                        <button className={bleedingDisorderStats.fac10Patients > 0 || bleedingDisorderStats.fac10Caretakers > 0 ||
                            bleedingDisorderStats.fac10FamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.fac10Patients > 0 || bleedingDisorderStats.fac10Caretakers > 0 ||
                                    bleedingDisorderStats.fac10FamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== fac10Selected) {
                                        updateSelection(fac10Selected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            10
                        </button>

                        <button className={bleedingDisorderStats.fac12Patients > 0 || bleedingDisorderStats.fac12Caretakers > 0 ||
                            bleedingDisorderStats.fac12FamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.fac12Patients > 0 || bleedingDisorderStats.fac12Caretakers > 0 ||
                                    bleedingDisorderStats.fac12FamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== fac12Selected) {
                                        updateSelection(fac12Selected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            12
                        </button>

                        <button className={bleedingDisorderStats.fac13Patients > 0 || bleedingDisorderStats.fac13Caretakers > 0 ||
                            bleedingDisorderStats.fac13FamilyMembers > 0 ? "bleeding-disorder-statistics-nav-category-button approved-button" :
                            "bleeding-disorder-statistics-nav-category-button denied-button"} onClick={() => {
                                if (bleedingDisorderStats.fac13Patients > 0 || bleedingDisorderStats.fac13Caretakers > 0 ||
                                    bleedingDisorderStats.fac13FamilyMembers > 0) {
                                    // Check for repeat clicks to prevent reloading same data
                                    if (selectedDisorder !== fac13Selected) {
                                        updateSelection(fac13Selected);
                                        setInCategory(true);
                                    }
                                }
                            }}>
                            13
                        </button>
                    </div>
                </>
            )
        }
    }

    return (
        <>
            <h1 className="header">Bleeding Disorder Statistics</h1>

            <div className="bleeding-disorder-statistics-container">

                {renderDisorderNav()}

                <div>
                    {renderStatisticCategoryViewer()}
                </div>

            </div>
        </>
    )


}